import { useCallback, useRef } from 'react';
import type { UseRecordQuestionActivityProps } from './types';
import { sdkLogger } from '../logger/logger';
import { QUESTION_ACTIVITY_STATE } from './types';
import { recordQuestionActivity } from './utils/recordQuestionActivity';

/**
 * Records question VIEWED activity with per-question deduplication.
 */
export function useRecordQuestionActivity({
  session,
  survey,
  getInstanceCreatedTime,
  onQuestionViewed,
}: UseRecordQuestionActivityProps) {
  const viewedQuestionIdsRef = useRef<Set<string>>(new Set());

  const recordView = useCallback(
    (questionIds: string[]) => {
      const instanceId = session?.instanceId?.trim();
      const surveyId = survey?.id?.trim();
      const responseId = session?.responseId?.trim();
      const instanceCreatedTime =
        session?.instanceCreatedTime ?? getInstanceCreatedTime();

      if (!instanceId || !surveyId || !responseId || !instanceCreatedTime) {
        sdkLogger.warn('Skipping question activity record — missing context', {
          hasInstanceId: Boolean(instanceId),
          hasSurveyId: Boolean(surveyId),
          hasResponseId: Boolean(responseId),
          hasInstanceCreatedTime: Boolean(instanceCreatedTime),
        });
        return;
      }

      const newQuestionIds = questionIds.filter(id => {
        const trimmed = id.trim();
        if (!trimmed || viewedQuestionIdsRef.current.has(trimmed)) return false;
        viewedQuestionIdsRef.current.add(trimmed);
        return true;
      });

      if (newQuestionIds.length === 0) return;

      for (const id of newQuestionIds) {
        const pageIndex = survey?.pages.findIndex(page =>
          page.questions.some(question => question.id === id)
        );
        onQuestionViewed?.(id, pageIndex !== undefined && pageIndex >= 0 ? pageIndex : undefined);
      }

      void recordQuestionActivity(instanceId, {
        questionIds: newQuestionIds,
        state: QUESTION_ACTIVITY_STATE.VIEWED,
        surveyId,
        responseId,
        instanceCreatedTime,
      });
    },
    [
      session?.instanceId,
      session?.responseId,
      session?.instanceCreatedTime,
      survey?.id,
      getInstanceCreatedTime,
      onQuestionViewed,
    ]
  );

  return { recordView };
}
